import { HttpStatusCode } from 'axios';

import { AnalysisFeedbackResponse, AnalysisFeedbackSubmission, AnalysisResponse } from '@futrli-sdk/predict_analysis';
import { API_PATHS, APIClients, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';

import { makeRequestWithResponse, OPTIONAL_BASE_URL } from './shared';

class AnalysisFeedbackRequestError extends Error {
  public readonly data: unknown;
  public readonly status: number;

  constructor(status: number, data: unknown) {
    super(`Analysis feedback request failed with status code ${status}`);
    this.name = 'AnalysisFeedbackRequestError';
    this.data = data;
    this.status = status;
  }
}

export const getAiAnalysis = async (workspaceId: string, organisationId: string): Promise<AnalysisResponse> => {
  const path = interpolatePathParams(API_PATHS.AI_ANALYSIS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<AnalysisResponse>({ url: path, method: REQUEST_METHODS.GET });
};

const getAnalysisFeedbackPath = (workspaceId: string, organisationId: string, analysisId: string): string =>
  interpolatePathParams(API_PATHS.AI_ANALYSIS_FEEDBACK, {
    [ROUTE_PARAMS.ANALYSIS_ID]: analysisId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

export const getAnalysisFeedback = async (
  workspaceId: string,
  organisationId: string,
  analysisId: string
): Promise<AnalysisFeedbackResponse | null> => {
  const response = await APIClients.getAPIClient().makeRequest<AnalysisFeedbackResponse>({
    payload: {
      request: {
        ...OPTIONAL_BASE_URL,
        method: REQUEST_METHODS.GET,
        url: getAnalysisFeedbackPath(workspaceId, organisationId, analysisId)
      }
    },
    validateStatus: () => true
  });

  if (response.status === HttpStatusCode.NotFound) {
    return null;
  }

  if (response.status !== HttpStatusCode.Ok) {
    throw new AnalysisFeedbackRequestError(response.status, response.data);
  }

  return response.data;
};

export const createAnalysisFeedback = async (
  workspaceId: string,
  organisationId: string,
  analysisId: string,
  data: AnalysisFeedbackSubmission
): Promise<AnalysisFeedbackResponse> =>
  makeRequestWithResponse<AnalysisFeedbackResponse>({
    data,
    method: REQUEST_METHODS.POST,
    url: getAnalysisFeedbackPath(workspaceId, organisationId, analysisId)
  });

export const replaceAnalysisFeedback = async (
  workspaceId: string,
  organisationId: string,
  analysisId: string,
  data: AnalysisFeedbackSubmission
): Promise<AnalysisFeedbackResponse> =>
  makeRequestWithResponse<AnalysisFeedbackResponse>({
    data,
    method: REQUEST_METHODS.PATCH,
    url: getAnalysisFeedbackPath(workspaceId, organisationId, analysisId)
  });
